import * as THREE from 'three';
import { getPois } from '../services/poiService';
import { snapToNavmesh } from '../services/navmeshService';
import { logEvent } from '../services/debugLog';
import type { PoseTransformService } from '../services/poseTransformService';
import type { POI } from '../types';

/**
 * Explore mode: floating name labels for EVERY POI, visible the moment the
 * user is localized — an AR directory of the space, no navigation needed.
 * Tapping a label starts navigation to that POI. Labels billboard toward the
 * camera and are hidden behind real walls by the scan-mesh occluder.
 */

const LABEL_HEIGHT_M = 1.9; // above breadcrumbs (floor) and media panels (1.5)
const LABEL_MAX_CHARS = 16;

export class ExploreLayer {
  private group = new THREE.Group();
  private labels: Array<{ obj: THREE.Mesh; poi: POI }> = [];
  private raycaster = new THREE.Raycaster();
  private loadStarted = false;

  constructor(
    private poseTransform: PoseTransformService,
    /** Tap on a label → e.g. start navigation to that POI. */
    private onSelect: (poi: POI) => void,
  ) {
    this.group.name = 'explore-layer';
    this.group.visible = false;
    this.poseTransform.mapRoot.add(this.group);
  }

  async load(): Promise<void> {
    if (this.loadStarted) return;
    this.loadStarted = true;
    const pois = await getPois();
    for (const poi of pois) {
      // Floor-anchor like MediaLayer: stored heights can be furniture/drifted.
      const probe = { x: poi.position.x, y: poi.position.y - 1.2, z: poi.position.z };
      const floor = snapToNavmesh(probe);
      const y = (floor ? floor.y : poi.position.y) + LABEL_HEIGHT_M;

      const label = new THREE.Mesh(
        new THREE.PlaneGeometry(0.7, 0.2),
        new THREE.MeshBasicMaterial({
          map: makeLabelTexture(poi.icon ?? '📍', poi.name),
          transparent: true,
          side: THREE.DoubleSide,
        }),
      );
      label.position.set(poi.position.x, y, poi.position.z);
      this.group.add(label);
      this.labels.push({ obj: label, poi });
    }
    logEvent('session', `explore layer ready: ${pois.length} label(s)`);
  }

  setVisible(on: boolean): void {
    this.group.visible = on;
  }

  get visible(): boolean {
    return this.group.visible;
  }

  /** Yaw-only billboard toward the camera. */
  update(camera: THREE.Camera): void {
    if (!this.group.visible || this.labels.length === 0) return;
    const camPos = new THREE.Vector3();
    camera.getWorldPosition(camPos);
    for (const { obj } of this.labels) {
      const p = new THREE.Vector3();
      obj.getWorldPosition(p);
      obj.lookAt(camPos.x, p.y, camPos.z);
    }
  }

  /** Tap → navigate to the tapped POI. Returns true if consumed. */
  handleTap(camera: THREE.Camera, ndcX: number, ndcY: number): boolean {
    if (!this.group.visible || this.labels.length === 0) return false;
    this.raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
    const hits = this.raycaster.intersectObjects(this.labels.map((l) => l.obj), false);
    if (hits.length === 0) return false;
    const hit = this.labels.find((l) => l.obj === hits[0].object);
    if (!hit) return false;
    logEvent('session', `explore label tapped: ${hit.poi.name}`);
    this.onSelect(hit.poi);
    return true;
  }
}

function makeLabelTexture(icon: string, name: string): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 448;
  canvas.height = 128;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = 'rgba(15, 23, 42, 0.9)';
  ctx.strokeStyle = '#38bdf8';
  ctx.lineWidth = 6;
  const r = 34;
  ctx.beginPath();
  ctx.moveTo(8 + r, 8);
  ctx.arcTo(canvas.width - 8, 8, canvas.width - 8, canvas.height - 8, r);
  ctx.arcTo(canvas.width - 8, canvas.height - 8, 8, canvas.height - 8, r);
  ctx.arcTo(8, canvas.height - 8, 8, 8, r);
  ctx.arcTo(8, 8, canvas.width - 8, 8, r);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#e2e8f0';
  ctx.font = 'bold 46px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const short = name.length > LABEL_MAX_CHARS ? `${name.slice(0, LABEL_MAX_CHARS - 1)}…` : name;
  ctx.fillText(`${icon} ${short}`, canvas.width / 2, canvas.height / 2 + 2);
  return new THREE.CanvasTexture(canvas);
}
